/**
 * Enhance Scope v2 (B-531) — a single proposal card in the triage queue.
 *
 * ADD    — the common case: CAT SEL + catalog dsc + tier pill + editable qty
 *          (with derivation formula) + reason/trigger/citation + approve/reject.
 * MODIFY — slice 3: swap an existing line's code. Side-by-side current → chosen
 *          candidate, radio-pill candidate picker (default preselected),
 *          "applies to N lines", an optional price-strip note, approve/reject.
 * FLAG   — Banner-styled, informational. With `dependent_ids` it gates other
 *          cards: Confirm reveals them, Reject hides + auto-rejects them.
 * unverified ADD — approve disabled until a real code is assigned in Review.
 * roomless ADD (room === null) — a room must be picked before approve enables.
 * applied — collapsed with an "Applied ✓" pill + Revert (MODIFY shows the swap).
 *
 * The card is presentational: triage state ({action, room, quantity, choiceIndex})
 * is owned by EnhancePanel and mutated through `onChange`. Tier pill colours
 * mirror SupplementSuggestions exactly (required→ok, recommended→warn,
 * consider→info).
 */
import type { EnhanceProposal, ProposalTier } from '../../../types';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Pill, type PillKind } from '../ui/pill';
import { Select } from '../ui/select';
import { IconCheck, IconWarn } from '../icons';

const TIER_PILL: Record<ProposalTier, PillKind> = {
  required: 'ok',
  recommended: 'warn',
  consider: 'info',
};

const TIER_LABEL: Record<ProposalTier, string> = {
  required: 'Required',
  recommended: 'Recommended',
  consider: 'Consider',
};

/** The user's in-flight triage decision for one proposal. */
export interface ProposalCardState {
  action?: 'approve' | 'reject';
  /** Room pick for a roomless proposal (needed before approve). */
  room?: string;
  /** Edited quantity override. */
  quantity?: number;
  /** MODIFY only: index into modify.candidates the user picked. */
  choiceIndex?: number;
}

/** Props for {@link ProposalCard}. */
export interface ProposalCardProps {
  proposal: EnhanceProposal;
  /** Parsed room names — the vocabulary for a roomless proposal's room pick. */
  rooms: string[];
  /** True when this proposal's line is already in the persisted parse. */
  applied: boolean;
  state: ProposalCardState;
  /** The user's answer to this proposal's confirm chip, if any. Owned by the
   *  panel (keyed by proposal_id). Slice 3 sends it to /apply for audit. */
  answer?: string;
  onChange: (_patch: Partial<ProposalCardState>) => void;
  /** Record the confirm-chip answer. */
  onAnswer: (_answer: string) => void;
  onRevert: () => void;
}

/** Resolve the MODIFY candidate index the card is currently showing. */
function chosenIndexOf(proposal: EnhanceProposal, state: ProposalCardState): number {
  if (!proposal.modify) return 0;
  return state.choiceIndex ?? proposal.modify.default_index;
}

/** A single ADD / MODIFY / FLAG proposal card. */
export function ProposalCard({
  proposal,
  rooms,
  applied,
  state,
  answer,
  onChange,
  onAnswer,
  onRevert,
}: ProposalCardProps) {
  const isModify = proposal.kind === 'MODIFY' && proposal.modify != null;
  const chosenIndex = chosenIndexOf(proposal, state);
  const chosen = proposal.modify
    ? proposal.modify.candidates[chosenIndex] ??
      proposal.modify.candidates[proposal.modify.default_index]
    : undefined;

  // The code label shown on collapsed rows. MODIFY reads as "current → chosen".
  const code = proposal.unverified
    ? 'no code matched'
    : isModify && chosen
      ? `${proposal.modify!.current_cat} ${proposal.modify!.current_sel} → ${chosen.cat} ${chosen.sel}`
      : `${proposal.cat} ${proposal.sel}`.trim();

  // --- Applied: collapsed row with Revert -------------------------------
  if (applied) {
    return (
      <div
        data-testid={`proposal-${proposal.proposal_id}`}
        className="flex items-center gap-3 rounded-[var(--r-md)] border border-[color-mix(in_oklab,var(--ok)_26%,transparent)] bg-[var(--ok-bg)] px-3 py-2"
      >
        <Pill kind="ok">
          <IconCheck /> Applied
        </Pill>
        <span className="whitespace-nowrap font-[var(--font-mono)] text-[12px] text-[var(--accent-ink)]">
          {code}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--tx-hi)]">
          {isModify && chosen ? chosen.description : proposal.description}
        </span>
        <Button
          size="sm"
          variant="ghost"
          data-testid={`revert-${proposal.proposal_id}`}
          onClick={onRevert}
        >
          Revert
        </Button>
      </div>
    );
  }

  // --- FLAG: informational Banner. Gates dependents when it has them. ----
  if (proposal.kind === 'FLAG') {
    const gating = proposal.dependent_ids.length > 0;
    const confirmed = state.action === 'approve';
    const rejected = state.action === 'reject';
    const depCount = proposal.dependent_ids.length;
    return (
      <Banner tone="warn" icon={<IconWarn />} className="items-start">
        <div data-testid={`proposal-${proposal.proposal_id}`}>
          <div className="flex items-center gap-2">
            <span className="font-[580] text-[12.5px] text-[var(--tx-hi)]">
              Needs a decision
            </span>
            {!gating && (
              // Disabled approve affordance so the "can't approve a flag" rule is
              // visible, not just implied.
              <Checkbox
                disabled
                checked={false}
                aria-label="Flag proposals cannot be approved"
                data-testid={`approve-${proposal.proposal_id}`}
              />
            )}
          </div>
          <div className="mt-1 text-[12.5px] text-[var(--tx-hi)]">{proposal.reason}</div>
          {proposal.trigger.type === 'narrative' ? (
            <div className="mt-1 text-[11px] text-[var(--tx-lo)]">
              <span className="text-[var(--tx-faint)]">from your notes: </span>
              <span className="italic">&ldquo;{proposal.trigger.detail}&rdquo;</span>
            </div>
          ) : (
            <div className="mt-1 text-[11px] text-[var(--tx-lo)]">
              Triggered by: {proposal.trigger.detail}
            </div>
          )}

          {gating && (
            <div className="mt-2">
              <div className="text-[11px] text-[var(--tx-lo)]">
                {confirmed
                  ? `Confirmed — ${depCount} related ${depCount === 1 ? 'addition is' : 'additions are'} shown below.`
                  : rejected
                    ? `Dismissed — ${depCount} related ${depCount === 1 ? 'addition' : 'additions'} hidden.`
                    : `${depCount} related ${depCount === 1 ? 'addition depends' : 'additions depend'} on this — confirm to review ${depCount === 1 ? 'it' : 'them'}.`}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <Button
                  size="sm"
                  variant={confirmed ? 'primary' : 'secondary'}
                  data-testid={`flag-confirm-${proposal.proposal_id}`}
                  onClick={() => onChange({ action: 'approve' })}
                >
                  {confirmed ? 'Confirmed' : 'Confirm'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`flag-reject-${proposal.proposal_id}`}
                  onClick={() => onChange({ action: 'reject' })}
                >
                  {rejected ? 'Dismissed' : 'Dismiss'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Banner>
    );
  }

  // --- shared triage gates (ADD + MODIFY) -------------------------------
  const isRoomless = !isModify && proposal.room == null;
  const roomChosen = isRoomless ? !!state.room : true;
  // A blocking confirm chip gates approve until the user answers it.
  const confirm = proposal.confirm;
  const answered = answer != null && answer !== '';
  const blockedByConfirm = !!(confirm?.blocking && !answered);
  const canApprove = !proposal.unverified && roomChosen && !blockedByConfirm;
  const approved = state.action === 'approve';
  const rejected = state.action === 'reject';

  if (rejected) {
    return (
      <div
        data-testid={`proposal-${proposal.proposal_id}`}
        className="flex items-center gap-3 rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-1)] px-3 py-2 opacity-60"
      >
        <span className="whitespace-nowrap font-[var(--font-mono)] text-[12px] text-[var(--tx-mid)]">
          {code}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--tx-mid)] line-through">
          {isModify && chosen ? chosen.description : proposal.description}
        </span>
        <span className="flex-none text-[11px] text-[var(--tx-lo)]">Rejected</span>
        <Button
          size="sm"
          variant="ghost"
          data-testid={`reopen-${proposal.proposal_id}`}
          onClick={() => onChange({ action: undefined })}
        >
          Undo
        </Button>
      </div>
    );
  }

  const qtyValue = state.quantity ?? proposal.quantity ?? undefined;

  return (
    <div
      data-testid={`proposal-${proposal.proposal_id}`}
      className={`rounded-[var(--r-md)] border bg-[var(--bg-1)] px-3 py-[10px] ${
        proposal.unverified
          ? 'border-[color-mix(in_oklab,var(--warn)_32%,transparent)]'
          : 'border-[var(--bd-subtle)]'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="pt-[2px]" title={blockedByConfirm ? 'answer the question first' : undefined}>
          <Checkbox
            checked={approved}
            disabled={!canApprove}
            aria-label={`Approve ${code}`}
            title={blockedByConfirm ? 'answer the question first' : undefined}
            data-testid={`approve-${proposal.proposal_id}`}
            onCheckedChange={(v) => onChange({ action: v === true ? 'approve' : undefined })}
          />
        </div>

        <div className="min-w-0 flex-1">
          {isModify && proposal.modify ? (
            // --- MODIFY header: current → proposed --------------------------
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="whitespace-nowrap font-[var(--font-mono)] text-[12px] text-[var(--tx-lo)] line-through">
                  {proposal.modify.current_cat} {proposal.modify.current_sel}
                </span>
                <span aria-hidden="true" className="text-[12px] text-[var(--tx-faint)]">
                  →
                </span>
                <span
                  className="whitespace-nowrap font-[var(--font-mono)] text-[12px] font-[580] text-[var(--accent-ink)]"
                  data-testid={`modify-proposed-${proposal.proposal_id}`}
                >
                  {chosen ? `${chosen.cat} ${chosen.sel}` : ''}
                </span>
                <Pill kind={TIER_PILL[proposal.tier]}>{TIER_LABEL[proposal.tier]}</Pill>
                <span className="min-w-0 flex-1 text-[12.5px] text-[var(--tx-hi)]">
                  {chosen ? chosen.description : proposal.description}
                </span>
              </div>

              {/* candidate picker (radio pills) */}
              {proposal.modify.candidates.length > 1 && (
                <div
                  className="mt-2 flex flex-wrap items-center gap-1.5"
                  role="radiogroup"
                  aria-label={`Replacement code for ${proposal.modify.current_cat} ${proposal.modify.current_sel}`}
                >
                  {proposal.modify.candidates.map((c, i) => {
                    const selected = i === chosenIndex;
                    return (
                      <button
                        key={`${c.cat}-${c.sel}-${i}`}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        data-testid={`modify-candidate-${proposal.proposal_id}-${i}`}
                        onClick={() => onChange({ choiceIndex: i })}
                        className={`h-7 rounded-[var(--r-full)] border px-3 font-[var(--font-mono)] text-[11px] font-[540] transition-colors ${
                          selected
                            ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent-ink)]'
                            : 'border-[var(--bd-strong)] bg-[var(--bg-2)] text-[var(--tx-mid)] hover:text-[var(--tx-hi)]'
                        }`}
                        title={c.description}
                      >
                        {c.cat} {c.sel}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* which lines get re-coded */}
              <div
                className="mt-2 text-[11px] text-[var(--tx-lo)]"
                data-testid={`modify-lines-${proposal.proposal_id}`}
              >
                Applies to {proposal.modify.line_numbers.length}{' '}
                {proposal.modify.line_numbers.length === 1 ? 'line' : 'lines'}
                {proposal.modify.line_numbers.length > 0 && (
                  <>: {proposal.modify.line_numbers.map((n) => `#${n}`).join(', ')}</>
                )}
              </div>

              {proposal.modify.price_strip && (
                <div
                  className="mt-1 text-[11px] text-[var(--tx-lo)]"
                  data-testid={`modify-pricestrip-${proposal.proposal_id}`}
                >
                  Price will be stripped — Xactimate re-prices this code on import.
                </div>
              )}
            </>
          ) : (
            // --- ADD header: code + tier + verification ---------------------
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="whitespace-nowrap font-[var(--font-mono)] text-[12px] text-[var(--accent-ink)]">
                  {code}
                </span>
                <Pill kind={TIER_PILL[proposal.tier]}>{TIER_LABEL[proposal.tier]}</Pill>
                {proposal.unverified && <Pill kind="warn">Unverified</Pill>}
                <span className="min-w-0 flex-1 text-[12.5px] text-[var(--tx-hi)]">
                  {proposal.description}
                </span>
              </div>

              {/* roomless: require a room pick */}
              {isRoomless && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[11px] text-[var(--tx-lo)]">Add to room</span>
                  <Select
                    aria-label={`Room for ${code}`}
                    data-testid={`room-${proposal.proposal_id}`}
                    value={state.room ?? ''}
                    onChange={(e) => onChange({ room: e.target.value || undefined })}
                    className="h-8 w-[200px]"
                  >
                    <option value="">Choose a room…</option>
                    {rooms.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              {/* quantity + derivation */}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  aria-label={`Quantity for ${code}`}
                  data-testid={`qty-${proposal.proposal_id}`}
                  value={qtyValue ?? ''}
                  onChange={(e) => {
                    const n = parseFloat(e.target.value);
                    onChange({ quantity: Number.isFinite(n) ? n : undefined });
                  }}
                  className="h-8 w-[110px] text-right"
                />
                <span className="text-[11.5px] text-[var(--tx-mid)]">{proposal.unit}</span>
                {proposal.quantity_derivation?.formula && (
                  <span className="text-[11px] text-[var(--tx-faint)]">
                    {proposal.quantity_derivation.formula}
                  </span>
                )}
              </div>
            </>
          )}

          {/* confirm chip — exceptions-only clarifying question that gates
              approve. Slice 3 sends the answer to /apply for audit. */}
          {confirm && (
            <div
              className="mt-2 rounded-[var(--r-md)] border border-[color-mix(in_oklab,var(--info)_28%,transparent)] bg-[var(--info-bg)] px-3 py-2"
              data-testid={`confirm-${proposal.proposal_id}`}
            >
              <div
                className="text-[12px] font-[560] text-[var(--tx-hi)]"
                data-testid={`confirm-q-${proposal.proposal_id}`}
              >
                {confirm.question}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(confirm.options.length > 0 ? confirm.options : ['Yes', 'No']).map((opt) => {
                  const selected = answer === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`confirm-option-${proposal.proposal_id}-${opt}`}
                      aria-pressed={selected}
                      onClick={() => onAnswer(opt)}
                      className={`h-7 rounded-[var(--r-full)] border px-3 text-[11.5px] font-[540] transition-colors ${
                        selected
                          ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent-ink)]'
                          : 'border-[var(--bd-strong)] bg-[var(--bg-2)] text-[var(--tx-mid)] hover:text-[var(--tx-hi)]'
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* reason / trigger / citation */}
          <div className="mt-2 text-[12px] text-[var(--tx-mid)]">{proposal.reason}</div>
          {proposal.trigger.type === 'narrative' ? (
            <div
              className="mt-1 text-[11px] text-[var(--tx-lo)]"
              data-testid={`trigger-${proposal.proposal_id}`}
            >
              <span className="text-[var(--tx-faint)]">from your notes: </span>
              <span className="italic text-[var(--tx-mid)]">
                &ldquo;{proposal.trigger.detail}&rdquo;
              </span>
            </div>
          ) : (
            <div
              className="mt-1 text-[11px] text-[var(--tx-lo)]"
              data-testid={`trigger-${proposal.proposal_id}`}
            >
              Triggered by: {proposal.trigger.detail}
            </div>
          )}
          <div className="mt-0.5 text-[11px] text-[var(--tx-faint)]">
            {proposal.citation ? proposal.citation : 'No citation'}
          </div>

          {/* per-card warnings */}
          {proposal.warnings.map((w, i) => (
            <div key={i} className="mt-1 text-[11px] text-[var(--warn)]">
              {w}
            </div>
          ))}

          {proposal.unverified && (
            <div className="mt-1 text-[11px] text-[var(--warn)]">
              Assign a real code in Review first, then this can be approved.
            </div>
          )}

          {/* reject affordance */}
          <div className="mt-2">
            <Button
              size="sm"
              variant="ghost"
              data-testid={`reject-${proposal.proposal_id}`}
              onClick={() => onChange({ action: 'reject' })}
            >
              Reject
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProposalCard;
